import { z } from 'zod';
import { businessProfileFieldsSchema, businessProfileSchema } from './business-profile';
import { calendarDateSchema, normalizedUuidSchema as uuidSchema } from './common';

const timestampSchema = z.iso.datetime();
const wonSchema = z.number().int().nonnegative();

/** 입금 기한(일) — 계좌이체 요청 시각부터. */
export const BANK_TRANSFER_DUE_DAYS = 7;
export const MAX_DEPOSITOR_NAME_LENGTH = 50;
export const MAX_BANK_MEMO_LENGTH = 200;

export const bankAccountSchema = z.strictObject({
  bank: z.string().trim().min(1).max(30),
  account: z.string().regex(/^[0-9-]{6,30}$/, '계좌번호는 숫자와 하이픈만 입력해 주세요.'),
  holder: z.string().trim().min(1).max(50),
});
export type BankAccount = z.infer<typeof bankAccountSchema>;

const depositorNameSchema = z.string().trim().min(1).max(MAX_DEPOSITOR_NAME_LENGTH);
const memoSchema = z.string().trim().max(MAX_BANK_MEMO_LENGTH).optional();

/** 계좌이체 주문에 붙이는 세금계산서 요청 — 저장 여부(`saveToProfile`)가 함께 온다. */
export const bankTransferTaxInvoiceInputSchema = businessProfileFieldsSchema.extend({
  saveToProfile: z.boolean(),
});
export type BankTransferTaxInvoiceInput = z.infer<typeof bankTransferTaxInvoiceInputSchema>;

export const placeBankTransferInputSchema = z.strictObject({
  orderId: uuidSchema,
  depositorName: depositorNameSchema,
  taxInvoice: bankTransferTaxInvoiceInputSchema.nullable(),
});
export type PlaceBankTransferInput = z.infer<typeof placeBankTransferInputSchema>;

/** 계좌이체 요청 결과 — 만든 결제 ID 와 입금 기한. */
export const placeBankTransferResultSchema = z.strictObject({
  paymentId: z.uuid(),
  dueAt: timestampSchema,
});
export type PlaceBankTransferResult = z.infer<typeof placeBankTransferResultSchema>;

export const updateBankTransferInputSchema = placeBankTransferInputSchema;
export type UpdateBankTransferInput = z.infer<typeof updateBankTransferInputSchema>;

export const abandonBankTransferInputSchema = z.strictObject({ orderId: uuidSchema });
export type AbandonBankTransferInput = z.infer<typeof abandonBankTransferInputSchema>;

export const bankTransferOptionSchema = z.strictObject({
  available: z.boolean(),
  bank: bankAccountSchema.nullable(),
  dueAt: timestampSchema,
  amount: wonSchema,
  defaultDepositorName: z.string(),
  profile: businessProfileSchema.nullable(),
});
export type BankTransferOption = z.infer<typeof bankTransferOptionSchema>;

export const BANK_TRANSFER_STATUSES = [
  'READY',
  'DONE',
  'ABORTED',
  'EXPIRED',
  'REFUND_PENDING',
  'CANCELED',
] as const;
export type BankTransferStatus = (typeof BANK_TRANSFER_STATUSES)[number];

export const bankTransferViewSchema = z.strictObject({
  status: z.enum(BANK_TRANSFER_STATUSES),
  bank: bankAccountSchema,
  amount: wonSchema,
  depositorName: z.string(),
  dueAt: timestampSchema,
  depositedOn: calendarDateSchema.nullable(),
  confirmedAt: timestampSchema.nullable(),
  refundAccount: bankAccountSchema.nullable(),
  refundedOn: calendarDateSchema.nullable(),
});
export type BankTransferView = z.infer<typeof bankTransferViewSchema>;

/** 고객 본인 입금 안내 편집용 — 주문 당시 요청 스냅샷. */
export const bankTransferDetailViewSchema = bankTransferViewSchema.extend({
  taxInvoiceRequest: businessProfileFieldsSchema.nullable(),
});
export type BankTransferDetailView = z.infer<typeof bankTransferDetailViewSchema>;

export const confirmDepositInputSchema = z.strictObject({
  orderId: uuidSchema,
  depositedOn: calendarDateSchema,
  depositorName: depositorNameSchema,
  memo: memoSchema,
});
export type ConfirmDepositInput = z.infer<typeof confirmDepositInputSchema>;

export const completeBankRefundInputSchema = z.strictObject({
  orderId: uuidSchema,
  refundedOn: calendarDateSchema,
  refundAccount: bankAccountSchema,
  memo: memoSchema,
});
export type CompleteBankRefundInput = z.infer<typeof completeBankRefundInputSchema>;

export const BANK_TRANSFER_ERROR_CODES = [
  'BANK_TRANSFER_UNAVAILABLE',
  'BANK_TRANSFER_ALREADY_OPEN',
  'PAYMENT_ALREADY_APPROVED',
  'DEPOSIT_NOT_PENDING',
  'REFUND_NOT_PENDING',
  'REFUND_ACCOUNT_REQUIRED',
  'BUSINESS_NUMBER_INVALID',
  'BUSINESS_NUMBER_CLOSED',
  'SUPPLIER_SETTINGS_INCOMPLETE',
] as const;
export type BankTransferErrorCode = (typeof BANK_TRANSFER_ERROR_CODES)[number];
